// POS (point of sale) state — cart, checkout fields and the offline sale queue.
import { create } from "zustand";
import { persist } from "zustand/middleware";
import api from "../services/api";
import {
  enqueueSale,
  getQueuedSales,
  syncQueuedSales,
} from "../services/posQueue";

const today = () => new Date().toISOString().slice(0, 10);

const buildPayload = (state) => ({
  customer_id: state.customerId || null,
  customer_name: state.customerName || null,
  customer_phone: state.customerPhone || null,
  customer_email: state.customerEmail || null,
  purchase_date: state.purchaseDate || today(),
  payment_method: state.paymentMethod || "cash",
  billing_type: state.billingType || "vat",
  vat_rate: state.vatRate ?? 13,
  notes: state.notes || null,
  line_items: state.cart.map((item) => ({
    name: item.name,
    type: item.type || "product",
    quantity: item.quantity || 1,
    unit_price: item.unit_price || 0,
    product_id: item.product_id || null,
  })),
});

const usePosStore = create(
  persist(
    (set, get) => ({
      // Cart
      cart: [],
      // Checkout fields
      customerId: null,
      customerName: null,
      customerPhone: null,
      customerEmail: null,
      purchaseDate: today(),
      paymentMethod: "cash",
      billingType: "vat",
      vatRate: 13,
      notes: "",
      // Queue / sync
      queuedSales: [],
      syncing: false,

      refreshQueuedCount: () => {
        const queued = getQueuedSales();
        set({ queuedSales: queued });
        return queued.length;
      },

      setCheckoutField: (field, value) => set({ [field]: value }),

      addToCart: (product, quantity = 1) => {
        if (!product?.id) return;
        const cart = [...get().cart];
        const existing = cart.find((item) => item.product_id === product.id);

        const stockLimit =
          typeof product.stock_quantity === "number" &&
          product.stock_quantity > 0
            ? product.stock_quantity
            : Infinity;

        if (existing) {
          const nextQty = Math.min(existing.quantity + quantity, stockLimit);
          existing.quantity = nextQty;
          existing.total = existing.unit_price * nextQty;
        } else {
          cart.push({
            product_id: product.id,
            name: product.name,
            type: product.type || "product",
            sku: product.sku || null,
            barcode: product.barcode || null,
            quantity: quantity,
            unit_price: parseFloat(product.price) || 0,
            total: (parseFloat(product.price) || 0) * quantity,
            stock: product.stock_quantity ?? null,
          });
        }

        set({ cart });
      },

      updateCartItem: (productId, patch) => {
        const cart = get().cart.map((item) => {
          if (item.product_id !== productId) return item;
          const next = { ...item, ...patch };
          next.quantity = Math.max(1, next.quantity || 1);
          next.total = next.unit_price * next.quantity;
          return next;
        });
        set({ cart });
      },

      removeFromCart: (productId) => {
        set({
          cart: get().cart.filter((item) => item.product_id !== productId),
        });
      },

      clearCart: () => set({ cart: [] }),

      /**
       * Complete the sale. Online: records the offline sale (stock is deducted
       * server-side) and generates the invoice. Offline: queues it locally.
       */
      completeSale: async ({ billToo = true } = {}) => {
        const state = get();
        if (state.cart.length === 0) {
          return { ok: false, error: "Cart is empty" };
        }
        const payload = buildPayload(state);

        const queue = () => {
          const entry = enqueueSale(payload, { billToo });
          get().refreshQueuedCount();
          get().clearCart();
          return { ok: true, queued: true, entry };
        };

        if (typeof navigator !== "undefined" && !navigator.onLine) {
          return queue();
        }

        try {
          const response = await api.post("/admin/offline-sales", payload);
          const sale = response.data?.data ?? response.data ?? {};

          let invoice = null;
          if (billToo && sale?.id) {
            try {
              const billRes = await api.post(
                `/admin/offline-sales/${sale.id}/generate-bill`,
                {
                  billing_type: payload.billing_type || "vat",
                  vat_rate: payload.vat_rate ?? 13,
                },
              );
              invoice = billRes.data?.data ?? billRes.data ?? null;
            } catch (error) {
              // Bill generation is best-effort; the sale itself is recorded.
              const billMsg =
                error?.response?.data?.message ||
                error?.message ||
                "Failed to generate bill";
              get().clearCart();
              return { ok: true, sale, invoice: null, billError: billMsg };
            }
          }

          get().clearCart();
          return { ok: true, sale, invoice };
        } catch (error) {
          // Network-level failure (no response) → queue for later sync
          if (!error.response) {
            return queue();
          }
          const msg =
            error?.response?.data?.message ||
            error?.response?.data?.errors?.line_items?.[0] ||
            error?.message ||
            "Failed to record sale";
          return { ok: false, error: msg };
        }
      },

      syncQueue: async () => {
        set({ syncing: true });
        try {
          const result = await syncQueuedSales({});
          get().refreshQueuedCount();
          set({ syncing: false });
          return result;
        } catch (error) {
          set({ syncing: false });
          return {
            synced: [],
            failed: [{ error: error?.message || "Sync failed" }],
          };
        }
      },
    }),
    {
      name: "kankali-pos-storage",
      partialize: (state) => ({
        cart: state.cart,
        customerId: state.customerId,
        customerName: state.customerName,
        customerPhone: state.customerPhone,
        customerEmail: state.customerEmail,
        purchaseDate: state.purchaseDate,
        paymentMethod: state.paymentMethod,
        billingType: state.billingType,
        vatRate: state.vatRate,
        notes: state.notes,
      }),
    },
  ),
);

export default usePosStore;
